import { collection, doc, addDoc, getDocs, getDoc, updateDoc, deleteDoc, query, where, orderBy, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore'
import { Comment, Reply } from '../types/course'
import { db } from '../config/firebaseConfig'

// Type for reply data as stored in Firestore
interface FirestoreReply {
  id: string
  userId: string
  userDisplayName?: string
  userEmail?: string // For backward compatibility
  userAvatar?: string
  comment: string
  timestamp: Timestamp | Date
  likes: number
  likedBy: string[]
  photo?: string // URL to attached image
}

// Type for comment data as stored in Firestore
interface FirestoreComment {
  courseId: string
  chapterId: string
  userId: string
  userDisplayName?: string
  userEmail?: string // For backward compatibility
  userAvatar?: string
  comment: string
  timestamp: Timestamp
  likes: number
  likedBy: string[]
  replies: FirestoreReply[]
  photo?: string // URL to attached image
}

// Comments collection reference
const commentsRef = collection(db, 'comments')

export const addComment = async (
  courseId: string,
  chapterId: string,
  userId: string,
  userDisplayName: string,
  userEmail: string,
  comment: string,
  photo?: string,
  userAvatar?: string
): Promise<void> => {
  const commentData: Partial<FirestoreComment> = {
    courseId,
    chapterId,
    userId,
    userDisplayName,
    userEmail, // For backward compatibility
    comment,
    timestamp: Timestamp.now(),
    likes: 0,
    likedBy: []
  }

  // Only include optional fields if they have values
  if (userAvatar) {
    commentData.userAvatar = userAvatar
  }
  if (photo) {
    commentData.photo = photo
  }

  await addDoc(commentsRef, commentData)
}

export const getComments = async (courseId: string, chapterId: string): Promise<Comment[]> => {
  const q = query(
    commentsRef,
    where('courseId', '==', courseId),
    where('chapterId', '==', chapterId)
  )

  const querySnapshot = await getDocs(q)
  const comments: Comment[] = []

  querySnapshot.forEach((doc) => {
    const data = doc.data()
    comments.push({
      id: doc.id,
      courseId: data.courseId,
      chapterId: data.chapterId,
      userId: data.userId,
      userDisplayName: data.userDisplayName || (data.userEmail ? data.userEmail.split('@')[0] : 'Anonymous'),
      userAvatar: data.userAvatar,
      comment: data.comment,
      timestamp: data.timestamp.toDate(),
      likes: data.likes || 0,
      likedBy: data.likedBy || [],
      photo: data.photo,
      replies: (data.replies || []).map((reply: FirestoreReply) => ({
        id: reply.id,
        userId: reply.userId,
        userDisplayName: reply.userDisplayName || (reply.userEmail ? reply.userEmail.split('@')[0] : 'Anonymous'),
        userAvatar: reply.userAvatar,
        comment: reply.comment,
        timestamp: reply.timestamp instanceof Timestamp ? reply.timestamp.toDate() : reply.timestamp,
        likes: reply.likes || 0,
        likedBy: reply.likedBy || [],
        photo: reply.photo
      }))
    })
  })

  // Sort comments by timestamp in memory (newest first)
  return comments.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
}

export const updateComment = async (commentId: string, newComment: string): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId)
  await updateDoc(commentRef, {
    comment: newComment
  })
}

export const deleteComment = async (commentId: string): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId)
  await deleteDoc(commentRef)
}

export const likeComment = async (commentId: string, userId: string): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId)

  // First get the current comment to check if user already liked it
  const commentSnap = await getDoc(commentRef)
  const commentData = commentSnap.data()

  if (!commentData) return

  const isLiked = commentData.likedBy?.includes(userId) || false

  if (isLiked) {
    // Unlike
    await updateDoc(commentRef, {
      likes: (commentData.likes || 0) - 1,
      likedBy: arrayRemove(userId)
    })
  } else {
    // Like
    await updateDoc(commentRef, {
      likes: (commentData.likes || 0) + 1,
      likedBy: arrayUnion(userId)
    })
  }
}

export const addReply = async (
  commentId: string,
  userId: string,
  userDisplayName: string,
  userEmail: string,
  reply: string,
  photo?: string,
  userAvatar?: string
): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId)

  const newReply: Partial<FirestoreReply> = {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9), // Simple ID generation
    userId,
    userDisplayName,
    userEmail // For backward compatibility
  }

  // Only include optional fields if they have values
  if (userAvatar) {
    newReply.userAvatar = userAvatar
  }
  if (photo) {
    newReply.photo = photo
  }

  // Set required fields
  newReply.comment = reply
  newReply.timestamp = Timestamp.now()
  newReply.likes = 0
  newReply.likedBy = []

  await updateDoc(commentRef, {
    replies: arrayUnion(newReply as FirestoreReply)
  })
}

export const updateReply = async (commentId: string, replyId: string, newReply: string): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId)

  // Get current comment
  const commentSnap = await getDoc(commentRef)
  const commentData = commentSnap.data()

  if (!commentData?.replies) return

  // Update the specific reply
  const updatedReplies = commentData.replies.map((reply: FirestoreReply) => {
    if (reply.id === replyId) {
      return {
        id: reply.id,
        userId: reply.userId,
        userDisplayName: reply.userDisplayName,
        userEmail: reply.userEmail, // Keep for backward compatibility
        comment: newReply,
        timestamp: reply.timestamp,
        likes: reply.likes || 0,
        likedBy: reply.likedBy || [],
        photo: reply.photo
      }
    }
    return reply
  })

  await updateDoc(commentRef, {
    replies: updatedReplies
  })
}

export const deleteReply = async (commentId: string, replyId: string): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId)

  // Get current comment
  const commentSnap = await getDoc(commentRef)
  const commentData = commentSnap.data()

  if (!commentData?.replies) return

  // Remove the specific reply
  const updatedReplies = commentData.replies.filter((reply: FirestoreReply) => reply.id !== replyId)

  await updateDoc(commentRef, {
    replies: updatedReplies
  })
}

export const likeReply = async (commentId: string, replyId: string, userId: string): Promise<void> => {
  const commentRef = doc(db, 'comments', commentId)

  // Get current comment
  const commentSnap = await getDoc(commentRef)
  const commentData = commentSnap.data()

  if (!commentData?.replies) return

  // Update the specific reply's likes
  const updatedReplies = commentData.replies.map((reply: FirestoreReply) => {
    if (reply.id === replyId) {
      const isLiked = reply.likedBy?.includes(userId) || false
      if (isLiked) {
        return {
          ...reply,
          likes: (reply.likes || 0) - 1,
          likedBy: reply.likedBy.filter((id: string) => id !== userId)
        }
      } else {
        return {
          ...reply,
          likes: (reply.likes || 0) + 1,
          likedBy: [...(reply.likedBy || []), userId]
        }
      }
    }
    return reply
  })

  await updateDoc(commentRef, {
    replies: updatedReplies
  })
}